/* React base class and ref helper */
import { Component, createRef } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router HOC and shared utilities */
import { withRouter, utils, applySeo } from "../../helpers"

/* Shared UI components */
import { Account } from "../../components"

/* Icon component */
import Icon from "../../components/Icon"

/* Profile API: read, update, avatar upload/remove and account deactivation */
import { get, update, avatar as uploadAvatar, removeAvatar, remove } from "../../api/Me"

/* i18n */
import { translate } from "../../translates"

/* Auth API: end the session on the server */
import { logout } from "../../api/Auth"

/* Page Profile */
class Profile extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            name: "",
            email: "",
            phone: "",
            birth: "",
            avatar: (props.user && props.user.avatar) || "",
            uploading: false,
            saving: false,
            confirm: null,
            busy: false,
            touched: false,
            toast: ""
        }

        /* File input */
        this.fileInput = createRef()
    }

    componentDidMount = () => {

        /* SEO */
        applySeo({ title: `Профиль — Bookify`, description: `Настройки вашего профиля в Bookify.`, path: `/profile`, robots: `noindex, nofollow` })

        /* Scroll */
        window.scroll(0, 0)

        /* Get Profile */
        get(this.props.token).then((response) => {
            if (response && response.status === 200) {
                const user = response.data
                this.setState({ name: user.name || "", email: user.email || "", phone: user.phone || "", birth: user.birth || "", avatar: user.avatar || "" })
                /* Refresh the header avatar too (older sessions may hold a stale value) */
                this.props.updateUser({ avatar: user.avatar || null })
            }
        })
    }

    /* Draw Toast */
    _toast = (message) => {

        /* Show a short auto-hiding message */
        this.setState({ toast: message })
        setTimeout(() => this.setState({ toast: "" }), 2500)
    }

    /* Draw Set */
    _set = (field, value) => {

        /* Update a single field */
        this.setState({ [field]: value })
    }

    /* Draw Valid */
    _valid = () => {

        /* Fields */
        const { name } = this.state

        /* Name must be at least two characters */
        return name.trim().length >= 2
    }

    /* Draw Save */
    _save = () => {

        /* Fields */
        const { name, email, birth, saving } = this.state
        const { token, updateUser } = this.props

        /* Validate */
        this.setState({ touched: true })
        if (saving || !this._valid()) {
            return
        }

        /* Save */
        this.setState({ saving: true })
        update(token, { name: name.trim(), email: email.trim(), birth }).then((response) => {
            this.setState({ saving: false })
            if (response && response.status === 200) {
                updateUser({ name: name.trim(), email: email.trim() })
                this._toast(translate("Профиль сохранён"))
            }
        })
    }

    /* Draw Pick File */
    _pickFile = () => {

        /* Open the hidden file dialog */
        if (this.fileInput.current) {
            this.fileInput.current.click()
        }
    }

    /* Draw On File */
    _onFile = (event) => {

        /* Fields */
        const { token, updateUser } = this.props
        const input = event.target
        const file = input.files && input.files[0]

        /* Upload the chosen image */
        if (!file) {
            return
        }
        this.setState({ uploading: true })
        uploadAvatar(token, file).then((response) => {
            if (response && response.status === 200 && response.data && response.data.avatar) {
                this.setState({ avatar: response.data.avatar })
                updateUser({ avatar: response.data.avatar })
                this._toast(translate("Фото обновлено"))
            }
            else {
                this._toast(translate("Не удалось загрузить фото"))
            }
            this.setState({ uploading: false })
            input.value = ""
        })
    }

    /* Draw Ask Remove Photo */
    _askRemovePhoto = () => {

        /* Confirm before removing the photo */
        this.setState({ confirm: "photo" })
    }

    /* Draw Ask Delete Account */
    _askDeleteAccount = () => {

        /* Confirm before deactivating the account */
        this.setState({ confirm: "account" })
    }

    /* Draw Close Confirm */
    _closeConfirm = () => {

        /* Dismiss the confirmation modal */
        this.setState({ confirm: null })
    }

    /* Draw Remove Photo */
    _removePhoto = () => {

        /* Fields */
        const { busy } = this.state
        const { token, updateUser } = this.props

        /* Remove the avatar on the server */
        if (busy) {
            return
        }
        this.setState({ busy: true })
        removeAvatar(token).then((response) => {
            this.setState({ busy: false })
            if (response && (response.status === 204 || response.status === 200)) {
                this.setState({ avatar: "", confirm: null })
                updateUser({ avatar: null })
                this._toast(translate("Фото удалено"))
            }
        })
    }

    /* Draw Delete Account */
    _deleteAccount = () => {

        /* Fields */
        const { busy } = this.state
        const { token, removeToken, removeUser, clearFavourites, navigate } = this.props

        /* Deactivate the account, then sign out locally */
        if (busy) {
            return
        }
        this.setState({ busy: true })
        remove(token).then((response) => {
            if (response && (response.status === 204 || response.status === 200)) {
                logout(token)
                removeToken()
                removeUser()
                clearFavourites()
                navigate("/")
            }
            else {
                this.setState({ busy: false })
            }
        })
    }

    /* Draw Head */
    _head = () => {

        /* Fields */
        const { name, phone, avatar, uploading } = this.state
        const { user } = this.props
        const source = avatar || (user && user.avatar) || ""

        return (
            <div className="profile__head">
                <div className="profile__avatar-wrap">
                    <span className="avatar profile__avatar">
                        <img src={source || utils.FALLBACK_AVATAR} alt="" onError={utils.onAvatarError} />
                    </span>
                    <button className="profile__avatar-badge" aria-label="Загрузить фото" onClick={this._pickFile}><Icon name="image" size={16} /></button>
                    <input ref={this.fileInput} type="file" accept="image/*" hidden onChange={this._onFile} />
                </div>
                <div>
                    <div className="profile__name">{name || translate("Без имени")}</div>
                    <div className="profile__phone">{phone}</div>
                    <div className="profile__photo-actions">
                        <button className="btn btn--ghost" disabled={uploading} onClick={this._pickFile}>
                            <Icon name="image" size={16} /> {uploading ? translate("Загрузка…") : translate("Загрузить фото")}
                        </button>
                        {source && (
                            <button className="btn btn--danger-ghost" onClick={this._askRemovePhoto}>
                                <Icon name="trash" size={16} /> {translate("Удалить")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    /* Draw Form */
    _form = () => {

        /* Fields */
        const { name, email, phone, birth, touched } = this.state
        const nameError = touched && name.trim().length < 2

        return (
            <div className="form-grid">
                <div className="field">
                    <label className="field__label">{translate("Ваше имя")}</label>
                    <input className={`input${nameError ? " input--error" : ""}`} value={name} onChange={(event) => this._set("name", event.target.value)} />
                    {nameError && <div className="field__err">{translate("Укажите имя")}</div>}
                </div>
                <div className="field">
                    <label className="field__label">{translate("Email (необязательно)")}</label>
                    <input className="input" placeholder="hello@bookify.kz" value={email} onChange={(event) => this._set("email", event.target.value)} />
                </div>
                <div className="field">
                    <label className="field__label">{translate("Телефон")}</label>
                    <input className="input" value={phone} disabled />
                    <div className="field__hint">{translate("Смена номера — через подтверждение по коду")}</div>
                </div>
                <div className="field">
                    <label className="field__label">{translate("Дата рождения")}</label>
                    <input className="input" type="date" value={birth} onChange={(event) => this._set("birth", event.target.value)} />
                </div>
            </div>
        )
    }

    /* Draw Confirm Modal */
    _confirmModal = () => {

        /* Fields */
        const { confirm, busy } = this.state

        /* Nothing to confirm */
        if (!confirm) {
            return null
        }

        /* Copy differs for photo removal and account deactivation */
        const isAccount = confirm === "account"
        const title = isAccount ? translate("Удалить аккаунт?") : translate("Удалить фото?")
        const text = isAccount ? translate("Вы потеряете доступ к записям, отзывам и сертификатам. Восстановить аккаунт можно только через поддержку.") : translate("Фото профиля будет удалено.")
        const label = isAccount ? translate("Удалить аккаунт") : translate("Удалить")
        const action = isAccount ? this._deleteAccount : this._removePhoto

        return (
            <div className="modal-backdrop" onClick={this._closeConfirm}>
                <div className="modal modal--sm" onClick={(event) => event.stopPropagation()}>
                    <div className="modal__body">
                        <div className="booking-cancel">
                            <div className="booking-cancel__icon"><Icon name="trash" size={28} /></div>
                            <h3 className="booking-cancel__title">{title}</h3>
                            <p className="booking-cancel__text">{text}</p>
                            <div className="booking-cancel__actions">
                                <button className="btn btn--ghost btn--lg" disabled={busy} onClick={this._closeConfirm}>{translate("Отмена")}</button>
                                <button className="btn btn--danger btn--lg" disabled={busy} onClick={action}>{busy ? translate("Удаляем…") : label}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    render = () => {

        /* Fields */
        const { toast, saving } = this.state

        return (
            <Account active="profile">
                <div className="content">
                    <h2 className="content__title">{translate("Редактировать профиль")}</h2>
                    <p className="content__subtitle">{translate("Эти данные будут видны организациям при записи")}</p>

                    {this._head()}
                    {this._form()}

                    <div className="profile__footer">
                        <button className="btn btn--danger-ghost" onClick={this._askDeleteAccount}>
                            <Icon name="trash" size={16} /> Удалить аккаунт
                        </button>
                        <button className="btn" disabled={saving} onClick={this._save}>
                            <Icon name="check" size={16} /> {saving ? translate("Сохраняю…") : translate("Сохранить")}
                        </button>
                    </div>
                </div>

                {this._confirmModal()}

                {toast && (
                    <div className="toast">
                        <div className="toast__check"><Icon name="check" size={16} /></div>
                        <div>{toast}</div>
                    </div>
                )}
            </Account>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        user: state.user,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        updateUser: data => dispatch({ type: "UPDATE_USER", payload: data }),
        removeToken: () => dispatch({ type: "REMOVE_TOKEN" }),
        removeUser: () => dispatch({ type: "REMOVE_USER" }),
        clearFavourites: () => dispatch({ type: "CLEAR_FAVOURITE" })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Profile))
